import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import {
  Deal, Client, OrderItem, Product, Pipeline, PipelineStage,
  dealsAPI, clientsAPI, productsAPI, pipelineAPI, stockAlertsAPI,
  OrderSourceChannel, PaymentType, PaymentStatus, FulfillmentPriority,
  InventoryCheckStatus, PickingStatus, ShippingStatus,
  DeliveryConfirmationStatus, OrderIssueType, OrderIssueStatus, OrderClosureStatus
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowLeftIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
  PlusIcon,
  TrashIcon,
  ShoppingCartIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, Button, Spinner, Badge } from '../ui';

interface DealFormProps {
  isEdit?: boolean;
  preselectedClientId?: number;
}

const DealForm: React.FC<DealFormProps> = ({ isEdit = false, preselectedClientId: preselectedClientIdProp }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();

  // Get preselectedClientId from either prop or location state
  const preselectedClientId = preselectedClientIdProp || (location.state as any)?.preselectedClientId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [clients, setClients] = useState<Client[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState<{
    id: number;
    name: string;
    type: string;
    email?: string;
  } | null>(null);

  // Check if we're in admin context
  const isAdminContext = location.pathname.startsWith('/admin');

  // Helper function to generate correct paths
  const getDealPath = (path: string) => {
    return isAdminContext ? `/admin${path}` : path;
  };
  const [formData, setFormData] = useState({
    clientId: '',
    amount: '',
    currency: 'USD',
    description: '',
    details: '',
    notes: '',
    pipelineId: '',
    stageId: '',
    probability: '',
    closeDate: '',
    forecastCategory: 'PIPELINE' as 'PIPELINE' | 'BEST_CASE' | 'COMMIT' | 'OMITTED',
    // Order Processing Fields
    sourceChannel: '' as OrderSourceChannel | '',
    paymentType: '' as PaymentType | '',
    paymentStatus: 'PENDING' as PaymentStatus,
    paymentDueDate: '',
    poNumber: '',
    fulfillmentPriority: 'STANDARD' as FulfillmentPriority,
    targetShipDate: '',
    specialInstructions: '',
    internalNotes: '',
  });

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    orderReceipt: false,
    fulfillment: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Pipeline state
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [stages, setStages] = useState<PipelineStage[]>([]);

  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [useOrderItems, setUseOrderItems] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [productSearch, setProductSearch] = useState<{ [key: number]: string }>({});
  const [showProductDropdown, setShowProductDropdown] = useState<{ [key: number]: boolean }>({});

  // Inventory availability state
  interface InventoryInfo {
    available: number;
    status: 'available' | 'low' | 'out_of_stock' | 'partial';
    loading: boolean;
  }
  const [inventoryInfo, setInventoryInfo] = useState<{ [productId: number]: InventoryInfo }>({});

  const currencies = [
    { value: 'USD', label: 'USD - US Dollar', symbol: '$' },
    { value: 'EUR', label: 'EUR - Euro', symbol: '€' },
    { value: 'GBP', label: 'GBP - British Pound', symbol: '£' },
    { value: 'CAD', label: 'CAD - Canadian Dollar', symbol: 'C$' },
    { value: 'AUD', label: 'AUD - Australian Dollar', symbol: 'A$' },
    { value: 'JPY', label: 'JPY - Japanese Yen', symbol: '¥' },
  ];

  useEffect(() => {
    loadPipelines();
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      fetchDeal();
    }
  }, [isEdit, id]);

  useEffect(() => {
    if (formData.pipelineId) {
      loadStages(parseInt(formData.pipelineId));
    } else {
      setStages([]);
      setFormData(prev => ({ ...prev, stageId: '', probability: '' }));
    }
  }, [formData.pipelineId]);

  // Debounced client search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (clientSearch.trim().length >= 2 || clientSearch.trim().length === 0) {
        searchClients(clientSearch);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [clientSearch]);

  // Handle preselected client
  useEffect(() => {
    if (preselectedClientId && !isEdit) {
      // Fetch the preselected client
      const fetchPreselectedClient = async () => {
        try {
          const client = await clientsAPI.getById(preselectedClientId);
          setSelectedClient({
            id: client.id,
            name: client.name,
            type: client.type,
            email: client.email,
          });
          setClientSearch(client.name);
          setFormData(prev => ({ ...prev, clientId: client.id.toString() }));
        } catch (err) {
          console.error('Error fetching preselected client:', err);
        }
      };
      fetchPreselectedClient();
    }
  }, [preselectedClientId, isEdit]);

  const loadPipelines = async () => {
    try {
      const data = await pipelineAPI.getAll();
      const activePipelines = data.filter(p => p.isActive);
      setPipelines(activePipelines);

      // Set default pipeline if creating new deal
      if (!isEdit) {
        const defaultPipeline = activePipelines.find(p => p.isDefault);
        if (defaultPipeline) {
          setFormData(prev => ({ ...prev, pipelineId: defaultPipeline.id.toString() }));
        }
      }
    } catch (err: any) {
      console.error('Error loading pipelines:', err);
    }
  };

  const loadStages = async (pipelineId: number) => {
    try {
      const data = await pipelineAPI.getStages(pipelineId);
      setStages(data);
    } catch (err: any) {
      console.error('Error loading stages:', err);
      setStages([]);
    }
  };

  const searchClients = async (query: string) => {
    try {
      setLoadingClients(true);
      const data = await clientsAPI.search(query);
      setClients(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Error searching clients:', err);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchDeal = async () => {
    try {
      setLoading(true);
      const deal = await dealsAPI.getById(Number(id));
      setFormData({
        clientId: deal.clientId.toString(),
        amount: deal.amount,
        currency: deal.currency,
        description: deal.description,
        details: deal.details || '',
        notes: deal.notes || '',
        pipelineId: deal.pipelineId?.toString() || '',
        stageId: deal.stageId?.toString() || '',
        probability: deal.probability?.toString() || '',
        closeDate: deal.closeDate ? deal.closeDate.split('T')[0] : '',
        forecastCategory: deal.forecastCategory || 'PIPELINE',
        // Order Processing Fields
        sourceChannel: deal.sourceChannel || '',
        paymentType: deal.paymentType || '',
        paymentStatus: deal.paymentStatus || 'PENDING',
        paymentDueDate: deal.paymentDueDate ? deal.paymentDueDate.split('T')[0] : '',
        poNumber: deal.poNumber || '',
        fulfillmentPriority: deal.fulfillmentPriority || 'STANDARD',
        targetShipDate: deal.targetShipDate ? deal.targetShipDate.split('T')[0] : '',
        specialInstructions: deal.specialInstructions || '',
        internalNotes: deal.internalNotes || '',
      });

      // Set selected client
      if (deal.client) {
        setSelectedClient(deal.client);
        setClientSearch(deal.client.name);
      }

      // Load order items if they exist
      if (deal.orderItems && deal.orderItems.length > 0) {
        setOrderItems(deal.orderItems);
        setUseOrderItems(true);

        // Initialize product search state with existing item names
        const searchState: { [key: number]: string } = {};
        deal.orderItems.forEach((item, index) => {
          searchState[index] = item.name;
        });
        setProductSearch(searchState);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch deal');
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client);
    setFormData(prev => ({ ...prev, clientId: client.id.toString() }));
    setClientSearch(client.name);
    setShowClientDropdown(false);
    setFieldErrors(prev => ({ ...prev, clientId: '' }));
  };

  const handleClientSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setClientSearch(value);
    setShowClientDropdown(true);
    if (!value) {
      setSelectedClient(null);
      setFormData(prev => ({ ...prev, clientId: '' }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const fetchProducts = async (search: string = '') => {
    try {
      const data = await productsAPI.getAll({ search });
      setProducts(data);
    } catch (err) {
      console.error('Error fetching products:', err);
      setProducts([]);
    }
  };

  const handleProductSelect = async (index: number, product: Product) => {
    const updated = [...orderItems];
    const quantity = updated[index].quantity || 1;
    updated[index] = {
      ...updated[index],
      productId: product.id,
      name: product.name,
      sku: product.sku,
      quantity,
      unit: product.unit,
      price: updated[index].price || 0,
    };
    setOrderItems(updated);
    setProductSearch(prev => ({ ...prev, [index]: product.name }));
    setShowProductDropdown(prev => ({ ...prev, [index]: false }));

    // Check inventory availability
    await checkInventoryForProduct(product.id, quantity);
  };

  const checkInventoryForProduct = async (productId: number, quantity: number) => {
    setInventoryInfo(prev => ({
      ...prev,
      [productId]: { available: 0, status: 'available', loading: true }
    }));

    try {
      const result = await stockAlertsAPI.checkAvailability([{ productId, requiredQuantity: quantity }]);
      const detail = result.details[0];

      let status: 'available' | 'low' | 'out_of_stock' | 'partial' = 'available';
      if (detail.availableQuantity === 0) {
        status = 'out_of_stock';
      } else if (detail.availableQuantity < quantity) {
        status = 'partial';
      } else if (detail.availableQuantity < quantity * 2) {
        status = 'low';
      }

      setInventoryInfo(prev => ({
        ...prev,
        [productId]: { available: detail.availableQuantity, status, loading: false }
      }));
    } catch (err) {
      console.error('Error checking inventory:', err);
      setInventoryInfo(prev => ({
        ...prev,
        [productId]: { available: 0, status: 'available', loading: false }
      }));
    }
  };

  const handleProductSearchChange = (index: number, value: string) => {
    setProductSearch(prev => ({ ...prev, [index]: value }));
    setShowProductDropdown(prev => ({ ...prev, [index]: true }));

    // Update the item name
    const updated = [...orderItems];
    updated[index] = { ...updated[index], name: value, productId: undefined, unit: undefined };
    setOrderItems(updated);

    // Fetch products if search has at least 2 characters
    if (value.length >= 2) {
      fetchProducts(value);
    }
  };

  const addOrderItem = () => {
    setOrderItems([...orderItems, { name: '', quantity: 1, unit: '', price: 0, sku: '' }]);
  };

  const removeOrderItem = (index: number) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
    // Clean up search state for this index
    const newProductSearch = { ...productSearch };
    const newShowDropdown = { ...showProductDropdown };
    delete newProductSearch[index];
    delete newShowDropdown[index];
    setProductSearch(newProductSearch);
    setShowProductDropdown(newShowDropdown);
  };

  const updateOrderItem = (index: number, field: keyof OrderItem, value: string | number) => {
    const updated = [...orderItems];
    let newQuantity: number | undefined;

    // Ensure proper type conversion for numeric fields
    if (field === 'quantity') {
      const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
      newQuantity = isNaN(numValue) ? 1 : numValue;
      updated[index] = { ...updated[index], [field]: newQuantity };
    } else if (field === 'price') {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      updated[index] = { ...updated[index], [field]: isNaN(numValue) ? 0 : numValue };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }

    setOrderItems(updated);

    // Re-check inventory when quantity changes
    if (field === 'quantity' && updated[index].productId && newQuantity) {
      checkInventoryForProduct(updated[index].productId!, newQuantity);
    }
  };

  const calculateTotal = () => {
    return orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.clientId) {
      errors.clientId = 'Please select a client';
    }

    if (useOrderItems) {
      if (orderItems.length === 0) {
        errors.orderItems = 'Please add at least one order item';
      } else {
        const hasInvalidItems = orderItems.some(item =>
          !item.name.trim() || item.quantity <= 0 || item.price < 0
        );
        if (hasInvalidItems) {
          errors.orderItems = 'All order items must have a name, quantity > 0, and price >= 0';
        }
      }
    } else {
      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        errors.amount = 'Please enter a valid amount greater than 0';
      }
    }

    if (!formData.description.trim()) {
      errors.description = 'Deal title is required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const submitData: any = {
        clientId: Number(formData.clientId),
        currency: formData.currency,
        description: formData.description,
        details: formData.details || undefined,
        notes: formData.notes || undefined,
        pipelineId: formData.pipelineId ? Number(formData.pipelineId) : undefined,
        stageId: formData.stageId ? Number(formData.stageId) : undefined,
        probability: formData.probability ? Number(formData.probability) : undefined,
        closeDate: formData.closeDate || undefined,
        forecastCategory: formData.forecastCategory || undefined,
        // Order Processing Fields
        sourceChannel: formData.sourceChannel || undefined,
        paymentType: formData.paymentType || undefined,
        paymentStatus: formData.paymentStatus || undefined,
        paymentDueDate: formData.paymentDueDate || undefined,
        poNumber: formData.poNumber || undefined,
        fulfillmentPriority: formData.fulfillmentPriority || undefined,
        targetShipDate: formData.targetShipDate || undefined,
        specialInstructions: formData.specialInstructions || undefined,
        internalNotes: formData.internalNotes || undefined,
      };

      if (useOrderItems && orderItems.length > 0) {
        submitData.orderItems = orderItems.map(item => ({
          productId: item.productId || undefined,
          name: item.name,
          quantity: item.quantity,
          unit: item.unit || undefined,
          price: item.price,
          sku: item.sku || undefined,
        }));
      } else {
        submitData.amount = parseFloat(formData.amount);
      }

      if (isEdit && id) {
        await dealsAPI.update(Number(id), submitData);
        setSuccess('Deal updated successfully!');
      } else {
        await dealsAPI.create(submitData);
        setSuccess('Deal created successfully!');
      }

      setTimeout(() => navigate(getDealPath('/deals')), 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} deal`);
    } finally {
      setSaving(false);
    }
  };

  const getCurrencySymbol = (currency: string) => {
    return currencies.find(c => c.value === currency)?.symbol || '$';
  };

  const formatAmount = (amount: string) => {
    if (!amount) return '';
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    return num.toLocaleString();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('#clientSearch') && !target.closest('.client-dropdown')) {
        setShowClientDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-sm text-neutral-600">Loading clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex mb-4" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link
                to={getDealPath('/deals')}
                className="text-neutral-500 hover:text-neutral-700 transition-colors"
              >
                Orders
              </Link>
            </li>
            <li>
              <span className="text-neutral-400">/</span>
            </li>
            <li>
              <span className="text-neutral-900 font-medium">
                {isEdit ? 'Edit Order' : 'New Order'}
              </span>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(getDealPath('/deals'))}
            className="mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Orders
          </Button>

          <Card variant="gradient" className="overflow-hidden">
            <CardBody className="p-6">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="h-16 w-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-sm">
                    <CurrencyDollarIcon className="h-8 w-8 text-success-600" />
                  </div>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-neutral-900">
                    {isEdit ? 'Edit Order' : 'Create New Order'}
                  </h1>
                  <p className="text-sm text-neutral-600 mt-1">
                    {isEdit ? 'Update order information and details' : 'Add a new order to track your sales progress'}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Success Message */}
        {success && (
          <Card variant="bordered" className="mb-6 border-success-200 bg-success-50">
            <CardBody className="p-4">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-success-500 mr-3" />
                <p className="text-sm font-medium text-success-800">{success}</p>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Error Message */}
        {error && (
          <Card variant="bordered" className="mb-6 border-error-200 bg-error-50">
            <CardBody className="p-4">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-error-500 mr-3" />
                <p className="text-sm font-medium text-error-800">{error}</p>
              </div>
            </CardBody>
          </Card>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Client Selection Card - Only show if no preselected client */}
          {!preselectedClientId && (
            <Card variant="bordered">
              <CardBody className="p-6 sm:p-8">
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                  <div className="sm:col-span-6">
                    <h3 className="text-lg font-semibold leading-7 text-neutral-900 flex items-center">
                      <BuildingOfficeIcon className="h-5 w-5 mr-2 text-neutral-400" />
                      Client Information
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-neutral-600">
                      Select the client for this deal
                    </p>
                  </div>

                  {/* Client Selection */}
                  <div className="sm:col-span-6">
                    <label htmlFor="clientSearch" className="block text-sm font-medium leading-6 text-neutral-900">
                      Client *
                    </label>
                    <div className="mt-2 relative">
                      <input
                        type="text"
                        id="clientSearch"
                        name="clientSearch"
                        autoComplete="off"
                        required
                        className={`block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 transition-colors ${fieldErrors.clientId
                          ? 'ring-error-300 focus:ring-error-500'
                          : 'ring-neutral-300 focus:ring-primary-600'
                          }`}
                        value={clientSearch}
                        onChange={handleClientSearchChange}
                        onFocus={() => setShowClientDropdown(true)}
                        placeholder="Search for a client..."
                      />
                      {loadingClients && (
                        <div className="absolute right-3 top-3">
                          <Spinner size="sm" />
                        </div>
                      )}

                      {/* Dropdown */}
                      {showClientDropdown && clients.length > 0 && (
                        <div className="client-dropdown absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                          {clients.map((client) => (
                            <div
                              key={client.id}
                              onClick={() => handleClientSelect(client)}
                              className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-primary-50 transition-colors"
                            >
                              <div className="flex items-center">
                                <BuildingOfficeIcon className="h-5 w-5 text-neutral-400 mr-2" />
                                <div>
                                  <div className="font-medium text-neutral-900">{client.name}</div>
                                  <div className="text-sm text-neutral-500">
                                    {client.type} {client.email && `• ${client.email}`}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {showClientDropdown && !loadingClients && clients.length === 0 && clientSearch.length >= 2 && (
                        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-lg py-4 text-center text-sm text-neutral-500 ring-1 ring-black ring-opacity-5">
                          No clients found. Try a different search term.
                        </div>
                      )}

                      {fieldErrors.clientId && (
                        <p className="mt-2 text-sm text-error-600 flex items-center">
                          <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                          {fieldErrors.clientId}
                        </p>
                      )}
                    </div>

                    {/* Selected Client Info */}
                    {selectedClient && (
                      <div className="mt-4 p-4 bg-primary-50 rounded-lg border border-primary-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                                <BuildingOfficeIcon className="h-6 w-6 text-primary-600" />
                              </div>
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold text-neutral-900">{selectedClient.name}</h4>
                              <p className="text-sm text-neutral-600">{selectedClient.type}</p>
                              {selectedClient.email && (
                                <p className="text-xs text-neutral-500 mt-1">{selectedClient.email}</p>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedClient(null);
                              setClientSearch('');
                              setFormData(prev => ({ ...prev, clientId: '' }));
                            }}
                          >
                            Change
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Preselected Client Display - Show when client is preselected */}
          {preselectedClientId && selectedClient && (
            <Card variant="bordered">
              <CardBody className="p-6 sm:p-8">
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                  <div className="sm:col-span-6">
                    <h3 className="text-lg font-semibold leading-7 text-neutral-900 flex items-center">
                      <BuildingOfficeIcon className="h-5 w-5 mr-2 text-neutral-400" />
                      Client Information
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-neutral-600">
                      Order for the following client
                    </p>
                  </div>

                  {/* Preselected Client Display (Read-only) */}
                  <div className="sm:col-span-6">
                    <div className="p-4 bg-primary-50 rounded-lg border border-primary-200">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center">
                            <BuildingOfficeIcon className="h-7 w-7 text-primary-600" />
                          </div>
                        </div>
                        <div>
                          <h4 className="text-base font-semibold text-neutral-900">{selectedClient.name}</h4>
                          <p className="text-sm text-neutral-600">{selectedClient.type}</p>
                          {selectedClient.email && (
                            <p className="text-xs text-neutral-500 mt-1">{selectedClient.email}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Deal Details Card */}
          <Card variant="bordered">
            <CardBody className="p-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <h3 className="text-lg font-semibold leading-7 text-neutral-900 flex items-center">
                    <BanknotesIcon className="h-5 w-5 mr-2 text-neutral-400" />
                    Deal Details
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-600">
                    Financial information and deal specifics
                  </p>
                </div>

                {/* Order Items Toggle */}
                <div className="sm:col-span-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="useOrderItems"
                      checked={useOrderItems}
                      onChange={(e) => setUseOrderItems(e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                    />
                    <label htmlFor="useOrderItems" className="ml-2 block text-sm text-neutral-900">
                      Use order items (itemized list)
                    </label>
                  </div>
                </div>

                {/* Amount and Currency - Only show if not using order items */}
                {!useOrderItems && (
                  <div className="sm:col-span-4">
                    <label htmlFor="amount" className="block text-sm font-medium leading-6 text-neutral-900">
                      Deal Amount *
                    </label>
                    <div className="mt-2">
                      <div className="flex rounded-lg shadow-sm ring-1 ring-inset ring-neutral-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-600">
                        <span className="flex select-none items-center pl-3 text-neutral-500 sm:text-sm font-medium">
                          {getCurrencySymbol(formData.currency)}
                        </span>
                        <input
                          type="number"
                          name="amount"
                          id="amount"
                          step="0.01"
                          min="0"
                          required
                          className={`block flex-1 border-0 bg-transparent py-2.5 pl-2 text-neutral-900 placeholder:text-neutral-400 focus:ring-0 sm:text-sm sm:leading-6 ${fieldErrors.amount ? 'text-error-900' : ''
                            }`}
                          value={formData.amount}
                          onChange={handleChange}
                          placeholder="0.00"
                        />
                      </div>
                      {fieldErrors.amount && (
                        <p className="mt-2 text-sm text-error-600 flex items-center">
                          <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                          {fieldErrors.amount}
                        </p>
                      )}
                      {formData.amount && !fieldErrors.amount && (
                        <p className="mt-2 text-sm text-neutral-600">
                          Formatted: {getCurrencySymbol(formData.currency)}{formatAmount(formData.amount)}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Currency */}
                {!useOrderItems && (
                  <div className="sm:col-span-2">
                    <label htmlFor="currency" className="block text-sm font-medium leading-6 text-neutral-900">
                      Currency
                    </label>
                    <div className="mt-2">
                      <select
                        name="currency"
                        id="currency"
                        className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                        value={formData.currency}
                        onChange={handleChange}
                      >
                        {currencies.map((currency) => (
                          <option key={currency.value} value={currency.value}>
                            {currency.value}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mt-2">
                      <span className="text-xs text-neutral-500">
                        {currencies.find(c => c.value === formData.currency)?.label}
                      </span>
                    </div>
                  </div>
                )}

                {/* Description */}
                <div className="sm:col-span-6">
                  <label htmlFor="description" className="block text-sm font-medium leading-6 text-neutral-900">
                    Deal Title *
                  </label>
                  <div className="mt-2">
                    <div className="flex rounded-lg shadow-sm ring-1 ring-inset ring-neutral-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-600">
                      <span className="flex select-none items-center pl-3 text-neutral-500 sm:text-sm">
                        <DocumentTextIcon className="h-4 w-4" />
                      </span>
                      <input
                        type="text"
                        name="description"
                        id="description"
                        required
                        className={`block flex-1 border-0 bg-transparent py-2.5 pl-2 text-neutral-900 placeholder:text-neutral-400 focus:ring-0 sm:text-sm sm:leading-6 ${fieldErrors.description ? 'text-error-900' : ''
                          }`}
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Enter deal or order title (e.g., 'Software License Agreement', 'Q4 Marketing Campaign')"
                      />
                    </div>
                    {fieldErrors.description && (
                      <p className="mt-2 text-sm text-error-600 flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        {fieldErrors.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Order Items Card - Only show if using order items */}
          {useOrderItems && (
            <Card variant="bordered">
              <CardBody className="p-6 sm:p-8">
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                  <div className="sm:col-span-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold leading-7 text-neutral-900 flex items-center">
                          <ShoppingCartIcon className="h-5 w-5 mr-2 text-neutral-400" />
                          Order Items
                        </h3>
                        <p className="mt-1 text-sm leading-6 text-neutral-600">
                          Add items to this order
                        </p>
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={addOrderItem}
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Add Item
                      </Button>
                    </div>
                  </div>

                  {fieldErrors.orderItems && (
                    <div className="sm:col-span-6">
                      <p className="text-sm text-error-600 flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        {fieldErrors.orderItems}
                      </p>
                    </div>
                  )}

                  {orderItems.length === 0 ? (
                    <div className="sm:col-span-6 text-center py-8">
                      <ShoppingCartIcon className="mx-auto h-12 w-12 text-neutral-400" />
                      <p className="mt-2 text-sm text-neutral-500">No items added yet</p>
                    </div>
                  ) : (
                    <div className="sm:col-span-6 space-y-4">
                      {orderItems.map((item, index) => (
                        <div key={index} className="border border-neutral-200 rounded-lg p-4">
                          <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-12 sm:col-span-5 relative">
                              <label className="block text-sm font-medium text-neutral-700 mb-1">
                                Item Name *
                                {item.productId && (
                                  <Badge variant="success" size="sm" className="ml-2">From Products</Badge>
                                )}
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  value={productSearch[index] !== undefined ? productSearch[index] : item.name}
                                  onChange={(e) => handleProductSearchChange(index, e.target.value)}
                                  onFocus={() => {
                                    setShowProductDropdown(prev => ({ ...prev, [index]: true }));
                                    if (!productSearch[index] && !item.name) {
                                      fetchProducts();
                                    }
                                  }}
                                  className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm pr-8"
                                  placeholder="Search products or enter manually"
                                />
                                <MagnifyingGlassIcon className="absolute right-2 top-2.5 h-5 w-5 text-neutral-400" />
                              </div>

                              {/* Product Dropdown */}
                              {showProductDropdown[index] && products.length > 0 && (
                                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                  {products.map((product) => (
                                    <div
                                      key={product.id}
                                      onClick={() => handleProductSelect(index, product)}
                                      className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-primary-50"
                                    >
                                      <div className="flex flex-col">
                                        <span className="font-medium text-neutral-900">{product.name}</span>
                                        <span className="text-xs text-neutral-500">
                                          SKU: {product.sku}
                                          {product.unit && ` • Unit: ${product.unit}`}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="col-span-6 sm:col-span-2">
                              <label className="block text-sm font-medium text-neutral-700">Quantity *</label>
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateOrderItem(index, 'quantity', e.target.value)}
                                className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                              />
                            </div>
                            <div className="col-span-6 sm:col-span-2">
                              <label className="block text-sm font-medium text-neutral-700">Price *</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.price}
                                onChange={(e) => updateOrderItem(index, 'price', e.target.value)}
                                className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                              />
                            </div>
                            <div className="col-span-10 sm:col-span-2">
                              <label className="block text-sm font-medium text-neutral-700">SKU</label>
                              <input
                                type="text"
                                value={item.sku || ''}
                                onChange={(e) => updateOrderItem(index, 'sku', e.target.value)}
                                className="mt-1 block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                placeholder="Optional"
                              />
                            </div>
                            <div className="col-span-2 sm:col-span-1 flex items-end">
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => removeOrderItem(index)}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="mt-2 flex items-center justify-between">
                            <span className="text-sm text-neutral-600">
                              Subtotal: {getCurrencySymbol(formData.currency)}{(item.price * item.quantity).toFixed(2)}
                            </span>
                            {/* Inventory Status */}
                            {item.productId && inventoryInfo[item.productId] && (
                              <div className="flex items-center gap-2">
                                {inventoryInfo[item.productId].loading ? (
                                  <span className="text-xs text-neutral-400">Checking stock...</span>
                                ) : (
                                  <>
                                    {inventoryInfo[item.productId].status === 'available' && (
                                      <Badge variant="success" size="sm">
                                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                                        In Stock ({inventoryInfo[item.productId].available})
                                      </Badge>
                                    )}
                                    {inventoryInfo[item.productId].status === 'low' && (
                                      <Badge variant="warning" size="sm">
                                        <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                        Low Stock ({inventoryInfo[item.productId].available})
                                      </Badge>
                                    )}
                                    {inventoryInfo[item.productId].status === 'partial' && (
                                      <Badge variant="warning" size="sm">
                                        <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                        Partial ({inventoryInfo[item.productId].available} of {item.quantity})
                                      </Badge>
                                    )}
                                    {inventoryInfo[item.productId].status === 'out_of_stock' && (
                                      <Badge variant="error" size="sm">
                                        <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                        Out of Stock
                                      </Badge>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}

                      <div className="border-t border-neutral-200 pt-4">
                        <div className="flex justify-between items-center text-lg font-semibold">
                          <span>Total Amount:</span>
                          <span className="text-primary-600">
                            {getCurrencySymbol(formData.currency)}{calculateTotal().toFixed(2)} {formData.currency}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Pipeline & Forecast Card */}
          <Card variant="bordered">
            <CardBody className="p-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <h3 className="text-lg font-semibold leading-7 text-neutral-900 flex items-center">
                    <svg className="h-5 w-5 mr-2 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Pipeline & Forecast
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-600">
                    Track deal progress through your sales pipeline
                  </p>
                </div>

                {/* Pipeline */}
                <div className="sm:col-span-3">
                  <label htmlFor="pipelineId" className="block text-sm font-medium leading-6 text-neutral-900">
                    Pipeline
                  </label>
                  <div className="mt-2">
                    <select
                      name="pipelineId"
                      id="pipelineId"
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.pipelineId}
                      onChange={handleChange}
                    >
                      <option value="">Select a pipeline...</option>
                      {pipelines.map((pipeline) => (
                        <option key={pipeline.id} value={pipeline.id}>
                          {pipeline.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Stage */}
                <div className="sm:col-span-3">
                  <label htmlFor="stageId" className="block text-sm font-medium leading-6 text-neutral-900">
                    Stage
                  </label>
                  <div className="mt-2">
                    <select
                      name="stageId"
                      id="stageId"
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.stageId}
                      onChange={(e) => {
                        handleChange(e);
                        // Auto-set probability from stage
                        const stage = stages.find(s => s.id === parseInt(e.target.value));
                        if (stage) {
                          setFormData(prev => ({ ...prev, probability: stage.defaultProbability.toString() }));
                        }
                      }}
                      disabled={!formData.pipelineId}
                    >
                      <option value="">Select a stage...</option>
                      {stages.map((stage) => (
                        <option key={stage.id} value={stage.id}>
                          {stage.name} ({stage.defaultProbability}%)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Probability */}
                <div className="sm:col-span-2">
                  <label htmlFor="probability" className="block text-sm font-medium leading-6 text-neutral-900">
                    Win Probability (%)
                  </label>
                  <div className="mt-2">
                    <input
                      type="number"
                      name="probability"
                      id="probability"
                      min="0"
                      max="100"
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.probability}
                      onChange={handleChange}
                      placeholder="50"
                    />
                  </div>
                </div>

                {/* Close Date */}
                <div className="sm:col-span-2">
                  <label htmlFor="closeDate" className="block text-sm font-medium leading-6 text-neutral-900">
                    Expected Close Date
                  </label>
                  <div className="mt-2">
                    <input
                      type="date"
                      name="closeDate"
                      id="closeDate"
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.closeDate}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Forecast Category */}
                <div className="sm:col-span-2">
                  <label htmlFor="forecastCategory" className="block text-sm font-medium leading-6 text-neutral-900">
                    Forecast Category
                  </label>
                  <div className="mt-2">
                    <select
                      name="forecastCategory"
                      id="forecastCategory"
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.forecastCategory}
                      onChange={handleChange}
                    >
                      <option value="PIPELINE">Pipeline</option>
                      <option value="BEST_CASE">Best Case</option>
                      <option value="COMMIT">Commit</option>
                      <option value="OMITTED">Omitted</option>
                    </select>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Order Receipt & Payment Card (Collapsible) */}
          <Card variant="bordered">
            <CardBody className="p-6 sm:p-8">
              <button
                type="button"
                onClick={() => toggleSection('orderReceipt')}
                className="w-full flex items-center justify-between text-left"
              >
                <div className="flex items-center">
                  <BanknotesIcon className="h-5 w-5 mr-2 text-neutral-400" />
                  <div>
                    <h3 className="text-lg font-semibold leading-7 text-neutral-900">
                      Order Receipt & Payment
                    </h3>
                    <p className="text-sm leading-6 text-neutral-600">
                      Source channel, payment terms, and PO information
                    </p>
                  </div>
                </div>
                {expandedSections.orderReceipt ? (
                  <ChevronUpIcon className="h-5 w-5 text-neutral-400" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-neutral-400" />
                )}
              </button>

              {expandedSections.orderReceipt && (
                <div className="mt-6 grid max-w-2xl grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
                  {/* Source Channel */}
                  <div className="sm:col-span-3">
                    <label htmlFor="sourceChannel" className="block text-sm font-medium leading-6 text-neutral-900">
                      Order Source
                    </label>
                    <div className="mt-2">
                      <select
                        name="sourceChannel"
                        id="sourceChannel"
                        className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                        value={formData.sourceChannel}
                        onChange={handleChange}
                      >
                        <option value="">Select source...</option>
                        <option value="WEBSITE">Website</option>
                        <option value="EMAIL">Email</option>
                        <option value="PHONE">Phone</option>
                        <option value="IN_PERSON">In Person</option>
                        <option value="MARKETPLACE">Marketplace</option>
                        <option value="EDI">EDI</option>
                        <option value="API">API</option>
                        <option value="REFERRAL">Referral</option>
                        <option value="TRADE_SHOW">Trade Show</option>
                        <option value="SOCIAL_MEDIA">Social Media</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                  </div>

                  {/* PO Number */}
                  <div className="sm:col-span-3">
                    <label htmlFor="poNumber" className="block text-sm font-medium leading-6 text-neutral-900">
                      PO Number
                    </label>
                    <div className="mt-2">
                      <input
                        type="text"
                        name="poNumber"
                        id="poNumber"
                        className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                        value={formData.poNumber}
                        onChange={handleChange}
                        placeholder="Customer PO number"
                      />
                    </div>
                  </div>

                  {/* Payment Type */}
                  <div className="sm:col-span-2">
                    <label htmlFor="paymentType" className="block text-sm font-medium leading-6 text-neutral-900">
                      Payment Terms
                    </label>
                    <div className="mt-2">
                      <select
                        name="paymentType"
                        id="paymentType"
                        className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                        value={formData.paymentType}
                        onChange={handleChange}
                      >
                        <option value="">Select terms...</option>
                        <option value="PREPAID">Prepaid</option>
                        <option value="NET_30">Net 30</option>
                        <option value="NET_60">Net 60</option>
                        <option value="NET_90">Net 90</option>
                        <option value="COD">COD</option>
                        <option value="LETTER_OF_CREDIT">Letter of Credit</option>
                        <option value="WIRE_TRANSFER">Wire Transfer</option>
                        <option value="CREDIT_CARD">Credit Card</option>
                        <option value="PAYPAL">PayPal</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                  </div>

                  {/* Payment Status */}
                  <div className="sm:col-span-2">
                    <label htmlFor="paymentStatus" className="block text-sm font-medium leading-6 text-neutral-900">
                      Payment Status
                    </label>
                    <div className="mt-2">
                      <select
                        name="paymentStatus"
                        id="paymentStatus"
                        className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                        value={formData.paymentStatus}
                        onChange={handleChange}
                      >
                        <option value="PENDING">Pending</option>
                        <option value="PARTIAL">Partial</option>
                        <option value="PAID">Paid</option>
                        <option value="OVERDUE">Overdue</option>
                        <option value="REFUNDED">Refunded</option>
                        <option value="DISPUTED">Disputed</option>
                        <option value="CANCELLED">Cancelled</option>
                      </select>
                    </div>
                  </div>

                  {/* Payment Due Date */}
                  <div className="sm:col-span-2">
                    <label htmlFor="paymentDueDate" className="block text-sm font-medium leading-6 text-neutral-900">
                      Payment Due Date
                    </label>
                    <div className="mt-2">
                      <input
                        type="date"
                        name="paymentDueDate"
                        id="paymentDueDate"
                        className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                        value={formData.paymentDueDate}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Fulfillment & Priority Card (Collapsible) */}
          <Card variant="bordered">
            <CardBody className="p-6 sm:p-8">
              <button
                type="button"
                onClick={() => toggleSection('fulfillment')}
                className="w-full flex items-center justify-between text-left"
              >
                <div className="flex items-center">
                  <ClipboardDocumentListIcon className="h-5 w-5 mr-2 text-neutral-400" />
                  <div>
                    <h3 className="text-lg font-semibold leading-7 text-neutral-900">
                      Fulfillment & Priority
                    </h3>
                    <p className="text-sm leading-6 text-neutral-600">
                      Priority level, target dates, and special instructions
                    </p>
                  </div>
                </div>
                {expandedSections.fulfillment ? (
                  <ChevronUpIcon className="h-5 w-5 text-neutral-400" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-neutral-400" />
                )}
              </button>

              {expandedSections.fulfillment && (
                <div className="mt-6 grid max-w-2xl grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
                  {/* Fulfillment Priority */}
                  <div className="sm:col-span-3">
                    <label htmlFor="fulfillmentPriority" className="block text-sm font-medium leading-6 text-neutral-900">
                      Priority Level
                    </label>
                    <div className="mt-2">
                      <select
                        name="fulfillmentPriority"
                        id="fulfillmentPriority"
                        className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                        value={formData.fulfillmentPriority}
                        onChange={handleChange}
                      >
                        <option value="STANDARD">Standard</option>
                        <option value="EXPEDITED">Expedited</option>
                        <option value="RUSH">Rush</option>
                        <option value="CRITICAL">Critical</option>
                        <option value="HOLD">On Hold</option>
                      </select>
                    </div>
                  </div>

                  {/* Target Ship Date */}
                  <div className="sm:col-span-3">
                    <label htmlFor="targetShipDate" className="block text-sm font-medium leading-6 text-neutral-900">
                      Target Ship Date
                    </label>
                    <div className="mt-2">
                      <input
                        type="date"
                        name="targetShipDate"
                        id="targetShipDate"
                        className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                        value={formData.targetShipDate}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Special Instructions */}
                  <div className="sm:col-span-6">
                    <label htmlFor="specialInstructions" className="block text-sm font-medium leading-6 text-neutral-900">
                      Special Instructions
                    </label>
                    <div className="mt-2">
                      <textarea
                        name="specialInstructions"
                        id="specialInstructions"
                        rows={3}
                        className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                        value={formData.specialInstructions}
                        onChange={handleChange}
                        placeholder="Special handling, packaging, or delivery instructions..."
                      />
                    </div>
                  </div>

                  {/* Internal Notes */}
                  <div className="sm:col-span-6">
                    <label htmlFor="internalNotes" className="block text-sm font-medium leading-6 text-neutral-900">
                      Internal Notes
                    </label>
                    <div className="mt-2">
                      <textarea
                        name="internalNotes"
                        id="internalNotes"
                        rows={2}
                        className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                        value={formData.internalNotes}
                        onChange={handleChange}
                        placeholder="Internal notes (not visible to customer)..."
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Additional Information Card */}
          <Card variant="bordered">
            <CardBody className="p-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <h3 className="text-lg font-semibold leading-7 text-neutral-900 flex items-center">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-neutral-400" />
                    Additional Information
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-600">
                    Detailed information and notes about this deal
                  </p>
                </div>

                {/* Details */}
                <div className="sm:col-span-6">
                  <label htmlFor="details" className="block text-sm font-medium leading-6 text-neutral-900">
                    Deal Details & Specifications
                  </label>
                  <p className="mt-1 text-sm text-neutral-600">
                    Provide comprehensive information about the deal including specifications, requirements, terms, deliverables, and any other relevant details.
                  </p>
                  <div className="mt-2">
                    <textarea
                      name="details"
                      id="details"
                      rows={8}
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.details}
                      onChange={handleChange}
                      placeholder="Enter detailed information about the deal:
• Product/service specifications
• Delivery requirements and timeline
• Terms and conditions
• Technical requirements
• Scope of work
• Special considerations
• Any other relevant details..."
                    />
                  </div>
                </div>

                {/* Notes */}
                <div className="sm:col-span-6">
                  <label htmlFor="notes" className="block text-sm font-medium leading-6 text-neutral-900">
                    Notes
                  </label>
                  <div className="mt-2">
                    <textarea
                      name="notes"
                      id="notes"
                      rows={4}
                      className="block w-full rounded-lg border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Additional notes about the deal, terms, conditions, or important details..."
                    />
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Deal Summary Card */}
          {formData.clientId && formData.amount && formData.description && (
            <Card variant="gradient" className="border border-primary-200">
              <CardBody className="p-6 sm:p-8">
                <h3 className="text-lg font-semibold leading-7 text-neutral-900 flex items-center mb-4">
                  <CheckCircleIcon className="h-5 w-5 mr-2 text-success-500" />
                  Deal Summary
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-neutral-600">Client</dt>
                    <dd className="text-sm text-neutral-900 font-medium">
                      {selectedClient?.name}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-neutral-600">Amount</dt>
                    <dd className="text-lg font-bold text-success-600">
                      {getCurrencySymbol(formData.currency)}{formatAmount(formData.amount)}
                    </dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-neutral-600">Description</dt>
                    <dd className="text-sm text-neutral-900">{formData.description}</dd>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Form Actions */}
          <Card variant="bordered" className="sticky bottom-4">
            <CardBody className="p-4 sm:p-6">
              <div className="flex items-center justify-end gap-x-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate(getDealPath('/deals'))}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={saving}
                >
                  {isEdit ? 'Update Deal' : 'Create Deal'}
                </Button>
              </div>
            </CardBody>
          </Card>
        </form>
      </div>
    </div>
  );
};

export default DealForm;
